import { Permission } from "@/Common/Api/Employee/Dto/Permission";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";

class PermissionDependencyResolver {
    private static dependencies: Map<string, string[]>;
    private static dependents: Map<string, string[]>;

    /**
     * The permissions listed in the array below are expected to function
     * differently than a straightforward two-way dependency between a child and a parent permission.
     *
     * 1. If you check one of these permission's children, the parent permission is automatically checked.
     * 2. However, if you check one of these parent permissions, none of its children are checked.
     */
    private static legacyParent = ["DocumentTabPermission", "BillingTabPermissionCurrentUserOnly", "PatientTabPermission"];

    /**
     * Provides an array of permission IDs corresponding to the children of a specified parent.
     *
     * @param permission current permission
     */
    public static GetChildren(permission: Permission): string[] {
        if (PermissionDependencyResolver.legacyParent.includes(permission.Name)) {
            return [];
        }
        else {
            return this.GetDependents(permission);
        }
    }

    /**
     * Returns an array of permission resulting from provided changes with resolved dependend permissions.
     *
     * @currentPermissions current permissions.
     * @changingPermissions an array of changing permissions.
     * @isChecked controls if the changing permissions are being added or removed.
     */
    public static ResolvePermissionChangesAndDependencies(currentPermissions: string[], changingPermissions: Permission[], isChecked: boolean): string[] {
        if (!cacheStore.ClinicSettings.EnableUserSettingsUiImprovements)
            return PermissionDependencyResolver.LegacyResolvePermissionChangesAndDependencies(currentPermissions, changingPermissions, isChecked);

        let nextPermissions = [...(currentPermissions ?? [])];

        changingPermissions.forEach(permission => {
            if (isChecked) {
                nextPermissions = PermissionDependencyResolver.legacyParent.includes(permission.Name)
                    ? [...nextPermissions, permission?.Id]
                    : [...nextPermissions, permission?.Id, ...PermissionDependencyResolver.GetDependencies(permission), ...PermissionDependencyResolver.GetDependents(permission)];
            }
            else {
                nextPermissions = nextPermissions.filter(p => ![permission?.Id, ...PermissionDependencyResolver.GetDependents(permission)].contains(p));
                nextPermissions = nextPermissions.filter(p => !PermissionDependencyResolver.GetDanglingDependencies(permission, nextPermissions).contains(p));
            }
        });

        return nextPermissions.filter(p => !!p).distinct();
    }

    private static RegisterDependency(parentEnum: EnumMyleLoginPermission, childEnum: EnumMyleLoginPermission): void {
        const parent = Permission.GetByEnum(parentEnum);
        const child = Permission.GetByEnum(childEnum);

        const dependency = PermissionDependencyResolver.dependencies.get(child.Id) ?? [];
        PermissionDependencyResolver.dependencies.set(child.Id, [...dependency, parent.Id]);

        const dependent = PermissionDependencyResolver.dependents.get(parent.Id) ?? [];
        PermissionDependencyResolver.dependents.set(parent.Id, [...dependent, child.Id]);
    }

    private static InitializeDependencies(): void {
        PermissionDependencyResolver.dependencies = new Map<string, string[]>();
        PermissionDependencyResolver.dependents = new Map<string, string[]>();

        PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.PatientTabPermission, EnumMyleLoginPermission.PrescriptionRenewalTabPermission);

        PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.DocumentTabPermission, EnumMyleLoginPermission.PrescriptionRenewalTabPermission);
        PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.DocumentTabPermission, EnumMyleLoginPermission.InboundFaxPermission);

        PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.BillingTabPermissionCurrentUserOnly, EnumMyleLoginPermission.BillingCostPermission);
        PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.BillingTabPermissionCurrentUserOnly, EnumMyleLoginPermission.BillingTabPermission);
        PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.BillingTabPermissionCurrentUserOnly, EnumMyleLoginPermission.PrivateBillingPermission);
        PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.BillingTabPermissionCurrentUserOnly, EnumMyleLoginPermission.PublicBillingPermission);

        if (cacheStore.ClinicSettings.EnableUserSettingsUiImprovements) {
            PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.AdminTabPermission, EnumMyleLoginPermission.Administration_SiteTabPermission);
            PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.AdminTabPermission, EnumMyleLoginPermission.Administration_ServiceCombosTabPermission);
            PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.AdminTabPermission, EnumMyleLoginPermission.Administration_UsersTabPermission);
            PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.AdminTabPermission, EnumMyleLoginPermission.Administration_BillableServicesTabPermission);
            PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.AdminTabPermission, EnumMyleLoginPermission.Administration_CommunicationGroupsTabPermission);
            PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.AdminTabPermission, EnumMyleLoginPermission.Administration_QuestionnairesTabPermission);
            PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.AdminTabPermission, EnumMyleLoginPermission.Administration_LogsTabPermission);
            if (cacheStore.ClinicSettings.EnableExternalCommunicationV2) {
                PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.AdminTabPermission, EnumMyleLoginPermission.Administration_ExternalConsultationTabPermission);
            }
            if (cacheStore.ClinicSettings.EnableUserPatientAutoMergeRequest) {
                PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.AdminTabPermission, EnumMyleLoginPermission.Administration_PatientMergeRequestPermission);
            }
            if (cacheStore.ClinicSettings.EnableRoleBasedAccessControl) {
                PermissionDependencyResolver.RegisterDependency(EnumMyleLoginPermission.AdminTabPermission, EnumMyleLoginPermission.Administration_RolesTabPermission);
            }
        }
    }

    private static GetDependencies(permission: Permission): string[] {
        if (!this.dependencies)
            this.InitializeDependencies();

        return this.dependencies.get(permission?.Id) ?? [];
    }

    private static GetDependents(permission: Permission): string[] {
        if (!this.dependents)
            this.InitializeDependencies();

        return this.dependents.get(permission?.Id) ?? [];
    }

    private static LegacyResolvePermissionChangesAndDependencies(currentPermissions: string[], changingPermissions: Permission[], isChecked: boolean): string[] {
        const nextPermissions = [...(currentPermissions ?? [])];

        changingPermissions.forEach(changingPermission => {
            if (isChecked) {
                const documentPermission = cacheStore.Permissions.firstOrDefault(p => p.Name === "DocumentTabPermission");
                const patientPermission = cacheStore.Permissions.firstOrDefault(p => p.Name === "PatientTabPermission");
                const billingPermission = cacheStore.Permissions.firstOrDefault(p => p.Name === "BillingTabPermissionCurrentUserOnly");

                if (nextPermissions.indexOf(changingPermission.Id) == -1)
                    nextPermissions.push(changingPermission.Id);

                // If you check the InboundFaxPermission requires the DocumentTabPermission.
                if (
                    (changingPermission.Name === "InboundFaxPermission" || changingPermission.Name === "PrescriptionRenewalTabPermission")
                    && nextPermissions.indexOf(documentPermission.Id) == -1
                ) {
                    nextPermissions.push(documentPermission.Id);
                }

                // Prescription renewals require the patient permission.
                if (changingPermission.Name === "PrescriptionRenewalTabPermission" && nextPermissions.indexOf(patientPermission.Id) == -1) {
                    nextPermissions.push(patientPermission.Id);
                }

                // PrivateBillingPermission and PublicBillingPermission both require the billingPermission.
                if ((changingPermission.Name === "PrivateBillingPermission"
                || changingPermission.Name === "PublicBillingPermission") && nextPermissions.indexOf(billingPermission.Id) === -1
                ) {
                    nextPermissions.push(billingPermission.Id);
                }
            }
            else {
                const inboundFaxPermission = cacheStore.Permissions.firstOrDefault(p => p.Name === "InboundFaxPermission");
                const prescriptionRenewalPermission = cacheStore.Permissions.firstOrDefault(p => p.Name === "PrescriptionRenewalTabPermission");
                const privateBillingPermission = cacheStore.Permissions.firstOrDefault(p => p.Name === "PrivateBillingPermission");
                const publicBillingPermission = cacheStore.Permissions.firstOrDefault(p => p.Name === "PublicBillingPermission");
                const billingPermission = cacheStore.Permissions.firstOrDefault(p => p.Name === "BillingTabPermissionCurrentUserOnly");

                // If you remove DocumentTabPermission, remove InboundFaxPermission and PrescriptionRenewalTabPermission as well.
                if (changingPermission.Name === "DocumentTabPermission") {
                    if (nextPermissions.indexOf(inboundFaxPermission.Id) > -1) {
                        nextPermissions.splice(nextPermissions.indexOf(inboundFaxPermission.Id), 1);
                    }
                    if (nextPermissions.indexOf(prescriptionRenewalPermission.Id) > -1) {
                        nextPermissions.splice(nextPermissions.indexOf(prescriptionRenewalPermission.Id), 1);
                    }
                }

                // If you remove BillingTabPermissionCurrentUserOnly, remove PrivateBillingPermission and PublicBillingPermission as well.
                if (changingPermission.Name === "BillingTabPermissionCurrentUserOnly") {
                    if (nextPermissions.indexOf(privateBillingPermission.Id) > -1) {
                        nextPermissions.splice(nextPermissions.indexOf(privateBillingPermission.Id), 1);
                    }
                    if (nextPermissions.indexOf(publicBillingPermission.Id) > -1) {
                        nextPermissions.splice(nextPermissions.indexOf(publicBillingPermission.Id), 1);
                    }
                    if (nextPermissions.indexOf(billingPermission.Id) > -1) {
                        nextPermissions.splice(nextPermissions.indexOf(billingPermission.Id), 1);
                    }
                }

                // If you remove PatientTabPermission, remove PrescriptionRenewalTabPermission as well.
                if (changingPermission.Name === "PatientTabPermission" && nextPermissions.indexOf(prescriptionRenewalPermission.Id) > -1)
                    nextPermissions.splice(nextPermissions.indexOf(prescriptionRenewalPermission.Id), 1);

                if (nextPermissions.indexOf(changingPermission.Id) > -1)
                    nextPermissions.splice(nextPermissions.indexOf(changingPermission.Id), 1);
            }
        });
        return nextPermissions;
    }

    private static GetDanglingDependencies(changingPermission: Permission, currentPermissions: string[]): string[] {
        return PermissionDependencyResolver
            .GetDependencies(changingPermission)
            .filter((dependencyId: string) => PermissionDependencyResolver.IsPermissionDangling(
                Permission.GetById(dependencyId),
                currentPermissions
            ));
    }

    private static IsPermissionDangling(permission: Permission, currentPermissions: string[]): boolean {
        const dependents: string[] = PermissionDependencyResolver.GetDependents(permission);

        if (!dependents.any())
            return false;

        return !dependents.any((dependent: string) => currentPermissions.contains(dependent));
    }
}

export { PermissionDependencyResolver };
